'use client';

import Image from 'next/image';
import { Button } from './ui/button';
import { signOut, useSession } from 'next-auth/react';
import { AgentIdNavMenu } from './agent-id-nav-menu';
import { HeaderLabel } from './icons/HeaderLabel';
import { useRouter } from 'next/navigation';
import { Link } from './ui/link';
import { resetDataLayer } from '@/lib/utils';
import { useSetAtom } from 'jotai';
import { quote } from '@/state/quote';
import { plans as plansAtom } from '@/state/plans';
import { RESET } from 'jotai/utils';
import { enrollment } from '@/state/enrollment';
import { crossSell } from '@/state/crosssell';
import { useFlags } from 'launchdarkly-react-client-sdk';

export default function Header() {
  const { status, data } = useSession();
  const router = useRouter();
  const { multiProduct, oktaAuthentication } = useFlags();
  const logoUrl = data?.user.agency?.logo?.file;
  const resetUserQuote = useSetAtom(quote);
  const resetPlans = useSetAtom(plansAtom);
  const resetEnrollment = useSetAtom(enrollment);
  const resetCrosssell = useSetAtom(crossSell);

  // Determine quote URL based on feature flag
  const quoteUrl = multiProduct ? '/multi-product/quote' : '/quote';

  const handleSignOut = async () => {
    sessionStorage.clear();
    if (oktaAuthentication) {
      const response = await fetch('/api/auth/okta-signout');
      const logoutUrl = response.ok ? (await response.json()).logoutUrl : null;
      await signOut({ redirect: false });
      window.location.href = logoutUrl ?? '/';
    } else {
      signOut({ callbackUrl: '/' });
    }
  };

  function handleReset() {
    resetDataLayer();

    resetCrosssell(RESET);
    resetUserQuote(RESET);
    resetPlans(RESET);
    resetEnrollment(RESET);
  }

  return (
    <header className="w-full flex flex-row justify-between items-center py-5 px-7 bg-olive-50 border-b-1 border-olive-400 print:hidden">
      <div className="text-neutral-800 flex flex-row items-center gap-x-5">
        <Link href={status === 'authenticated' ? quoteUrl : '/'}>
          <Image
            src="https://images.ctfassets.net/gau1nv66ynug/5Dx7un7ad6f1T3ZBBBrDAe/e615c41101bb4a33fc1c4a9c4efcb8f4/Guardian-logo-dark.png"
            alt="guardian logo"
            width={150}
            height={100}
          />
        </Link>
        {logoUrl && (
          <>
            <HeaderLabel />
            <figure className="relative w-15 h-10">
              <Image
                src={logoUrl}
                className="object-contain"
                alt="partner logo"
                fill={true}
              />
            </figure>
          </>
        )}
      </div>
      <div className="flex justify-right space-x-7 items-center">
        {status === 'authenticated' && (
          <>
            <p className="font-effra text-blue-900 text-base">
              Questions? Call 1-866-569-9900
            </p>
            <p className="font-effra text-blue-900 text-base">
              Agent: {data.user.agentName}
            </p>
            <AgentIdNavMenu />
            <Button
              variant="cta"
              onClick={() => {
                handleReset();
                router.push(quoteUrl);
              }}
              data-ta="header-start-new-quote"
            >
              Start new quote
            </Button>
            <Button
              variant="secondary"
              className="p-3 text-md/150"
              onClick={handleSignOut}
              data-ta="header-sign-out"
            >
              Sign Out
            </Button>
          </>
        )}
      </div>
    </header>
  );
}
